import type { ReactNode } from "react"

import { cn } from "@/lib/utils"

type ModulePageHeaderProps = {
  title?: string
  subtitle?: string
  actions?: ReactNode
  className?: string
  /** Custom header row content (e.g. tender toolbar). Replaces title/actions layout. */
  children?: ReactNode
  /** Secondary metadata row below the main header (dates, references). */
  meta?: ReactNode
  /** Sticky toolbar styling for in-module workspace headers. */
  sticky?: boolean
}

/** Shared page header for DataWorkspace modules (admin, tender, analytics). */
export function ModulePageHeader({
  title,
  subtitle,
  actions,
  className,
  children,
  meta,
  sticky = false,
}: ModulePageHeaderProps) {
  return (
    <header
      className={cn(
        sticky
          ? "bg-background/80 sticky top-0 z-10 flex flex-col border-b px-6 py-3 backdrop-blur-sm"
          : "flex flex-col gap-1 px-4 py-4 sm:flex-row sm:items-start sm:justify-between",
        className
      )}
    >
      {children ?? (
        <>
          <div className="min-w-0 space-y-1">
            {title && <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>}
            {subtitle && <p className="text-muted-foreground text-sm">{subtitle}</p>}
          </div>
          {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
        </>
      )}
      {meta && (
        <div className="text-muted-foreground mt-0.5 flex items-center gap-3 pl-1 text-xs">
          {meta}
        </div>
      )}
    </header>
  )
}
